import { useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import { api, baseQty, displayRate, fmt, type Invoke, type Order } from '../api'
import { useSession } from '@argo/common/session'

type Action = 'send' | 'mint' | 'burn'

/**
 * One account. On a phone this reads top to bottom: what you hold, what you
 * can do, what is outstanding. Given two columns, the form moves to the side
 * and stays in view while the balances and orders scroll.
 */
export default function Account() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { me } = useSession()
  const account = decodeURIComponent(useParams().account ?? '')
  const issuer = me?.roles.includes('issuer') ?? false
  const needsPin = me?.pin.set ?? false

  const [action, setAction] = useState<Action>('send')
  const [to, setTo] = useState('')
  const [symbol, setSymbol] = useState('AUD')
  const [amount, setAmount] = useState('')
  const [pin, setPin] = useState('')
  const [note, setNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)

  const balances = useQuery({ queryKey: ['balances', account], queryFn: () => api.balances(account), refetchInterval: 10_000 })
  const orders = useQuery({ queryKey: ['orders', account], queryFn: () => api.orders(account), refetchInterval: 10_000 })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['balances', account] })
    qc.invalidateQueries({ queryKey: ['orders', account] })
    qc.invalidateQueries({ queryKey: ['graph'] })
  }

  const write = useMutation({
    mutationFn: () => {
      const values = action === 'send'
        ? { from: account, to, symbol, amount: Number(amount) }
        : { vault: account, symbol, amount: Number(amount) }
      return api.command<Invoke>('tuna', action, account, values, pin || undefined)
    },
    onSuccess: r => { setNote({ text: `${t('done')} · ${t('block')} ${r.block_number}`, kind: 'ok' }); setAmount(''); setTo(''); refresh() },
    onError: (e: Error) => setNote({ text: e.message, kind: 'err' }),
  })

  const cancel = useMutation({
    mutationFn: (o: Order) => api.command<Invoke>('tuna', 'cancel', account, {
      k: o.chain_key,
      r1: { x: account, y: o.give_symbol, z: Number(o.qty_remaining), o: '' },
      r2: { x: account, y: o.want_symbol, z: Number(o.price), o: '>=' },
    }, pin || undefined),
    onSuccess: () => { message.success(t('done')); refresh() },
    onError: (e: Error) => message.error(e.message),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setNote(null)
    if (!(Number(amount) > 0)) { setNote({ text: t('enterAmount'), kind: 'err' }); return }
    if (action === 'send' && !to.trim()) { setNote({ text: t('to'), kind: 'err' }); return }
    write.mutate()
  }

  const actions: Action[] = issuer ? ['send', 'mint', 'burn'] : ['send']

  return (
    <div className="page page-mid cols cols-side">
      <div className="stack">
        <div className="panel">
          <div className="panel-head">
            <span className="grow">{t('balance')}</span>
            <span className="chip accent">{account}</span>
          </div>
          <div className="panel-body flush">
            {balances.isLoading && <div className="panel-body"><div className="skeleton" style={{ height: 44 }} /></div>}
            {balances.data && balances.data.data.length === 0 && <div className="empty">{t('noData')}</div>}
            {balances.data?.data.map(b => (
              <div className="list-row" key={b.symbol}>
                <div className="grow"><span className="title">{b.symbol}</span></div>
                <span className="value">{fmt(b.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="grow">{t('myOrders')}</span>
            {orders.data?.data.length ? <span className="chip">{orders.data.data.length}</span> : null}
          </div>
          <div className="panel-body flush">
            {(orders.data?.data.length ?? 0) === 0 ? (
              <div className="empty">{t('noOrders')}</div>
            ) : orders.data?.data.map(o => (
              <div className="list-row" key={o.chain_key}>
                <span className={`chip ${o.side === 'buy' ? 'up' : 'down'}`}>{o.side === 'buy' ? t('buy') : t('sell')}</span>
                <div className="grow">
                  <div className="title num">{fmt(displayRate(o.price, o.side))}</div>
                  <div className="sub num">{fmt(baseQty(o), 4)} {o.side === 'sell' ? o.give_symbol : o.want_symbol}</div>
                </div>
                <button className="btn-ghost" disabled={cancel.isPending} onClick={() => cancel.mutate(o)}>{t('cancel')}</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="side">
        <div className="panel">
          <div className="panel-head"><span className="grow">{t(action)}</span></div>
          <div className="panel-body">
            <form onSubmit={submit}>
              {actions.length > 1 && (
                <div className="side-toggle" style={{ gridTemplateColumns: `repeat(${actions.length}, 1fr)` }}>
                  {actions.map(x => (
                    <button key={x} type="button" className={action === x ? 'on-buy' : ''}
                      aria-pressed={action === x} onClick={() => setAction(x)}>{t(x)}</button>
                  ))}
                </div>
              )}

              {action === 'send' && (
                <div className="field">
                  <label htmlFor="to">{t('to')}</label>
                  <div className="input-wrap">
                    <input id="to" value={to} onChange={e => setTo(e.target.value)} placeholder="bob"
                      style={{ fontWeight: 400 }} autoCapitalize="none" />
                  </div>
                </div>
              )}

              <div className="field">
                <label htmlFor="sym">{t('symbol')}</label>
                <div className="input-wrap">
                  <input id="sym" value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} />
                </div>
              </div>

              <div className="field">
                <label htmlFor="amt">{t('amount')}</label>
                <div className="input-wrap">
                  <input id="amt" inputMode="numeric" value={amount} placeholder="0"
                    onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
                  <span className="unit">{symbol}</span>
                </div>
              </div>

              {needsPin && (
                <div className="field">
                  <label htmlFor="apin">{t('pin')}</label>
                  <div className="input-wrap">
                    <input id="apin" type="password" inputMode="numeric" autoComplete="off" placeholder="••••"
                      value={pin} onChange={e => setPin(e.target.value.replace(/[^\d]/g, ''))} />
                  </div>
                </div>
              )}

              <button className="submit buy" type="submit" disabled={write.isPending}>
                {write.isPending ? t('loading') : t('submit')}
              </button>
              {note && <p className={`form-note ${note.kind}`}>{note.text}</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
